// Platform-aware shortcut labels (0051 S1a). Every Review handler accepts
// either modifier ((e.metaKey || e.ctrlKey)), so a hint names the one the
// founder's keyboard carries: the Command glyph on Apple platforms, "Ctrl"
// everywhere else. Each export reads the navigator at CALL time, never at
// module load, so a render (or a test that stubs the platform) sees the
// platform it runs on.

// The navigator fields read here. `userAgentData` is the newer Client Hints
// API (not in TypeScript's DOM lib); `platform` and `userAgent` are the older
// strings every browser still serves.
export type NavigatorLike = {
  userAgentData?: { platform?: string };
  platform?: string;
  userAgent?: string;
};

const APPLE_PLATFORM = /Mac|iPhone|iPad|iPod/i;

function currentNavigator(): NavigatorLike | undefined {
  return typeof globalThis.navigator === "undefined"
    ? undefined
    : (globalThis.navigator as unknown as NavigatorLike);
}

// isApplePlatform reads the FIRST non-empty of userAgentData.platform,
// platform and userAgent ("macOS", "MacIntel", "iPhone" are Apple; "Windows",
// "Win32", "Linux x86_64" are not). No navigator at all reads as not Apple.
export function isApplePlatform(nav: NavigatorLike | undefined = currentNavigator()): boolean {
  if (!nav) return false;
  const first = [nav.userAgentData?.platform, nav.platform, nav.userAgent].find(
    (s): s is string => typeof s === "string" && s !== "",
  );
  return first !== undefined && APPLE_PLATFORM.test(first);
}

// The modifier key's label: "⌘" on Apple, "Ctrl" otherwise.
export function modKeyLabel(nav?: NavigatorLike): string {
  return isApplePlatform(nav) ? "⌘" : "Ctrl";
}

// A modifier shortcut as a hint prints it: "⌘↵" on Apple, "Ctrl+↵" otherwise.
export function shortcut(key: string, nav?: NavigatorLike): string {
  return isApplePlatform(nav) ? "⌘" + key : "Ctrl+" + key;
}

// 0051 S1b: a keyboard hint shows only where a keyboard is likely - a screen
// at least 640 px wide whose primary pointer is fine (a mouse or a trackpad) -
// so a phone or a touch-only tablet never shows one. One whole literal per
// display value, because Tailwind's scanner reads whole class names.
export const KEY_HINT_INLINE = "hidden [@media(min-width:640px)_and_(pointer:fine)]:inline";
export const KEY_HINT_FLEX = "hidden [@media(min-width:640px)_and_(pointer:fine)]:flex";
